/**
 * Digital Insights Panel
 *
 * Subtab layout:
 *   Fingerprints — entity-first digital identity cards
 *   Activity     — raw digital event volume and hourly detail
 *   Deviations   — deviation score timeline and flagged anomalies
 *   Similarity   — digitally similar entities
 */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Tooltip,
  LinearProgress,
  useTheme,
  alpha,
  Stack,
  Tab,
  Tabs,
  Collapse,
  IconButton,
  Popover,
} from '@mui/material';
import {
  Smartphone as DigitalIcon,
  Apps as AppsIcon,
  Language as BrowsingIcon,
  Chat as CommunicationIcon,
  LocationOn as LocationIcon,
  Groups as GroupsIcon,
  Warning as WarningIcon,
  Fingerprint as FingerprintIcon,
  CompareArrows as CompareIcon,
  Timeline as TimelineIcon,
  BugReport as AnomalyIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
} from 'recharts';
import polService, {
  DigitalPattern,
  DigitalSimilarity,
  DigitalInsightsResponse,
  DigitalFingerprint,
  DigitalFingerprintSummary,
  ContributingFactor,
  HourlyEvent,
  HourlyDetailResponse,
} from '../../services/polService';

interface DigitalInsightsPanelProps {
  digitalPatterns: DigitalPattern[];
  digitalSimilarities: DigitalSimilarity[];
  digitalInsights: DigitalInsightsResponse | null;
  loading?: boolean;
  category: string;
  onCategoryChange: (category: string) => void;
  onEntitySelect?: (entityId: string | null) => void;
  selectedEntity?: string | null;
  entityIds?: string[];
  courseId?: string;
  daysBack?: number;
  startDate?: string;
  endDate?: string;
  mapSelectedCell?: { hour: number; day: number } | null;
  onDigitalEventSelect?: (event: HourlyEvent) => void;
  onDigitalEventsLoad?: (events: HourlyEvent[], slot?: { day: number; hour: number }) => void;
}

const DigitalInsightsPanel: React.FC<DigitalInsightsPanelProps> = ({
  digitalPatterns,
  digitalSimilarities,
  digitalInsights,
  loading = false,
  // category + onCategoryChange remain in the props API for parent compatibility,
  // but the category filter UI was removed; digital indicators are display-only now.
  category: _category,
  onCategoryChange: _onCategoryChange,
  onEntitySelect,
  selectedEntity,
  entityIds,
  courseId,
  daysBack = 30,
  startDate,
  endDate,
  mapSelectedCell,
  onDigitalEventSelect,
  onDigitalEventsLoad,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [subtab, setSubtab] = useState(0);
  const [expandedAnomaly, setExpandedAnomaly] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [heatmapAnchor, setHeatmapAnchor] = useState<HTMLElement | null>(null);
  const [heatmapCell, setHeatmapCell] = useState<{ day: number; hour: number; count: number } | null>(null);
  const [hourlyDetail, setHourlyDetail] = useState<HourlyDetailResponse | null>(null);
  const [hourlyDetailLoading, setHourlyDetailLoading] = useState(false);
  const [hourlyDetailSlot, setHourlyDetailSlot] = useState<{ day: number; hour: number } | null>(null);
  const [fingerprintMapEntity, setFingerprintMapEntity] = useState<string | null>(null);
  const lastActivityFilterKeyRef = useRef<string>('');
  const lastFingerprintFilterKeyRef = useRef<string>('');

  const scrollbarSx = {
    '&::-webkit-scrollbar': { width: '6px', height: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)',
      borderRadius: '3px',
    },
    scrollbarWidth: 'thin' as const,
    scrollbarColor: isDark ? 'rgba(255, 255, 255, 0.3) transparent' : 'rgba(0, 0, 0, 0.25) transparent',
  };

  const rechartsTooltipThemeProps = useMemo(
    () => ({
      contentStyle: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: '0.75rem',
      },
      labelStyle: { color: theme.palette.text.primary },
      itemStyle: { color: theme.palette.text.secondary },
    }),
    [theme],
  );

  const cardSx = {
    p: 1.5,
    borderRadius: 2,
    bgcolor: isDark ? '#0d1117' : '#fff',
    border: `1px solid ${isDark ? '#30363d' : '#e0e0e0'}`,
    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    height: '100%',
  };

  const ANOMALY_THRESHOLD = 0.6;

  const filteredAnomalies = useMemo(() => {
    if (!digitalInsights?.anomalies) return [];
    return digitalInsights.anomalies.filter(a => a.anomaly_score >= ANOMALY_THRESHOLD);
  }, [digitalInsights?.anomalies]);

  const rawActivityCounts = digitalInsights?.activity_counts;
  const rawActivityTotal = rawActivityCounts?.all ?? (
    digitalInsights?.activity_baseline?.reduce((total, row) => (
      total + row.reduce((rowTotal, value) => rowTotal + value, 0)
    ), 0) ?? 0
  );

  const hasInsights = digitalInsights && (
    digitalInsights.entity_profiles.length > 0 ||
    filteredAnomalies.length > 0 ||
    digitalInsights.summary.total_entities_scored > 0
  );
  const digitalInsightFingerprints = digitalInsights?.entity_fingerprints ?? {};
  const fingerprintSummaries = digitalInsights?.entity_fingerprint_summaries ?? {};
  const hasInsightFingerprints = Object.keys(digitalInsightFingerprints).length > 0;
  const hasDigitalActivity = (
    rawActivityTotal > 0 ||
    !!digitalInsights?.activity_baseline?.some(row => row.some(v => v > 0)) ||
    hasInsightFingerprints ||
    digitalPatterns.length > 0
  );

  const timelineData = useMemo(() => {
    if (!digitalInsights?.anomaly_timeline) return [];
    return digitalInsights.anomaly_timeline.map(entry => ({
      date: entry.date.slice(5),
      avgScore: Math.round(entry.avg_score * 100),
      anomalies: entry.anomalies_detected,
      entities: entry.entities_scored,
    }));
  }, [digitalInsights?.anomaly_timeline]);

  const entityRanking = useMemo(() => {
    if (!digitalInsights?.entity_profiles) return [];
    return digitalInsights.entity_profiles.filter(p => p.total_days_scored >= 3);
  }, [digitalInsights?.entity_profiles]);

  const hourlyPatterns = useMemo(() => {
    if (digitalInsights?.activity_baseline && digitalInsights.activity_baseline.some(row => row.some(v => v > 0))) {
      const grid = digitalInsights.activity_baseline;
      const maxCount = Math.max(...grid.flat(), 1);
      return { grid, maxCount };
    }
    const grid: number[][] = Array(7).fill(null).map(() => Array(24).fill(0));
    digitalPatterns.forEach(p => {
      if (p.peak_hour !== null && p.peak_day !== null) {
        grid[p.peak_day][p.peak_hour] += p.occurrence_count;
      }
    });
    const maxCount = Math.max(...grid.flat(), 1);
    return { grid, maxCount };
  }, [digitalInsights?.activity_baseline, digitalPatterns]);

  const fallbackFingerprints = useMemo<Record<string, DigitalFingerprint[]>>(() => {
    const grouped: Record<string, DigitalFingerprint[]> = {};
    digitalPatterns.forEach((pattern) => {
      if (!grouped[pattern.entity_id]) grouped[pattern.entity_id] = [];
      grouped[pattern.entity_id].push({
        course_id: pattern.course_id,
        category: pattern.category,
        name: pattern.name,
        description: pattern.description,
        pattern_type: pattern.pattern_type,
        occurrence_count: pattern.occurrence_count,
        confidence: pattern.confidence,
        peak_hour: pattern.peak_hour,
        peak_day: pattern.peak_day,
        time_of_day: pattern.time_of_day,
        days_active: pattern.days_active,
        usage_frequency: pattern.usage_frequency,
        location_name: pattern.location_name,
        metadata: pattern.metadata,
      });
    });
    Object.keys(grouped).forEach((entityId) => {
      grouped[entityId].sort((a, b) => b.confidence - a.confidence || b.occurrence_count - a.occurrence_count);
    });
    return grouped;
  }, [digitalPatterns]);

  const effectiveFingerprints = hasInsightFingerprints ? digitalInsightFingerprints : fallbackFingerprints;

  const fingerprintEntityIds = useMemo(() => {
    return Object.keys(effectiveFingerprints).sort((a, b) => {
      if (selectedEntity === a) return -1;
      if (selectedEntity === b) return 1;
      const aScore = fingerprintSummaries[a]?.confidence_summary?.max ?? effectiveFingerprints[a]?.[0]?.confidence ?? 0;
      const bScore = fingerprintSummaries[b]?.confidence_summary?.max ?? effectiveFingerprints[b]?.[0]?.confidence ?? 0;
      return bScore - aScore || a.localeCompare(b);
    });
  }, [effectiveFingerprints, fingerprintSummaries, selectedEntity]);

  const selectedDateAnomalies = useMemo(() => {
    if (!selectedDate) return [];
    return filteredAnomalies.filter(a => a.date?.startsWith(selectedDate));
  }, [selectedDate, filteredAnomalies]);

  const entityIdsKey = useMemo(() => entityIds?.join(',') ?? '', [entityIds]);
  const eventFilterKey = useMemo(
    () => [courseId ?? '', daysBack, startDate ?? '', endDate ?? '', entityIdsKey].join('|'),
    [courseId, daysBack, endDate, entityIdsKey, startDate],
  );

  const handleTimelineClick = useCallback((data: any) => {
    if (data?.activePayload?.[0]?.payload?.date) {
      const clickedDate = data.activePayload[0].payload.date;
      setSelectedDate(prev => prev === clickedDate ? null : clickedDate);
    }
  }, []);

  const loadHourlyDetail = useCallback(async (
    day: number,
    hour: number,
    count: number,
    anchorEl?: HTMLElement | null,
    overrideEntityIds?: string[],
  ) => {
    if (day < 0 || day > 6 || hour < 0 || hour > 23) return;

    setHeatmapAnchor(anchorEl ?? null);
    setHeatmapCell({ day, hour, count });
    setHourlyDetailSlot({ day, hour });
    setHourlyDetailLoading(true);
    setFingerprintMapEntity(null);
    lastActivityFilterKeyRef.current = `${eventFilterKey}|${day}|${hour}`;

    try {
      const idsParam = overrideEntityIds ? overrideEntityIds.join(',') : (entityIdsKey || undefined);
      const data = await polService.getHourlyDetail(
        day,
        hour,
        courseId,
        daysBack,
        idsParam,
        undefined,
        startDate,
        endDate,
      );
      setHourlyDetail(data);
      onDigitalEventsLoad?.(data.events, { day, hour });
    } catch (err) {
      setHourlyDetail(null);
      onDigitalEventsLoad?.([], { day, hour });
    } finally {
      setHourlyDetailLoading(false);
    }
  }, [courseId, daysBack, endDate, entityIdsKey, eventFilterKey, onDigitalEventsLoad, startDate]);

  const handleHeatmapClick = useCallback(async (event: React.MouseEvent<HTMLElement>, day: number, hour: number, count: number) => {
    // Toggle off if clicking the already-selected cell
    if (hourlyDetailSlot?.day === day && hourlyDetailSlot?.hour === hour) {
      setHeatmapAnchor(null);
      setHeatmapCell(null);
      setHourlyDetailSlot(null);
      setHourlyDetail(null);
      onDigitalEventsLoad?.([]);
      return;
    }

    await loadHourlyDetail(day, hour, count, event.currentTarget);
  }, [hourlyDetailSlot, loadHourlyDetail, onDigitalEventsLoad]);

  // Dismiss the heatmap popover when the user scrolls so it doesn't block the pane
  useEffect(() => {
    if (!heatmapAnchor) return;
    const dismiss = () => { setHeatmapAnchor(null); setHeatmapCell(null); };
    const scrollParent = heatmapAnchor.closest('[style*="overflow"], [class*="overflow"]')
      || heatmapAnchor.closest('.MuiTabPanel-root')
      || document;
    const target = scrollParent === document ? window : scrollParent;
    target.addEventListener('scroll', dismiss, { passive: true });
    return () => target.removeEventListener('scroll', dismiss);
  }, [heatmapAnchor]);

  const getHourColor = (count: number, maxCount: number) => {
    if (count === 0) return isDark ? '#161b22' : '#ebedf0';
    const intensity = Math.min(count / maxCount, 1);
    if (intensity < 0.25) return isDark ? '#0e4429' : '#9be9a8';
    if (intensity < 0.5) return isDark ? '#006d32' : '#40c463';
    if (intensity < 0.75) return isDark ? '#26a641' : '#30a14e';
    return isDark ? '#39d353' : '#216e39';
  };

  const formatHour = (h: number) => {
    if (h === 0) return '12a';
    if (h === 12) return '12p';
    return h < 12 ? `${h}a` : `${h - 12}p`;
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const getActivityCellCount = useCallback((day: number, hour: number) => {
    return hourlyPatterns.grid[day]?.[hour] ?? 0;
  }, [hourlyPatterns.grid]);

  const getDefaultActivityCell = useCallback(() => {
    if (mapSelectedCell) {
      return {
        day: mapSelectedCell.day,
        hour: mapSelectedCell.hour,
        count: getActivityCellCount(mapSelectedCell.day, mapSelectedCell.hour),
        source: 'map' as const,
      };
    }

    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    return {
      day,
      hour,
      count: getActivityCellCount(day, hour),
      source: 'current' as const,
    };
  }, [getActivityCellCount, mapSelectedCell]);

  const loadFingerprintMapEvents = useCallback(async (entityId: string) => {
    setFingerprintMapEntity(entityId);
    lastFingerprintFilterKeyRef.current = `${eventFilterKey}|${entityId}`;
    try {
      const data = await polService.getDigitalEvents(
        courseId,
        daysBack,
        entityId,
        1000,
        startDate,
        endDate,
      );
      onDigitalEventsLoad?.(data.events, { day: 0, hour: 0 });
    } catch (err) {
      onDigitalEventsLoad?.([], { day: 0, hour: 0 });
    }
  }, [courseId, daysBack, endDate, eventFilterKey, onDigitalEventsLoad, startDate]);

  const activitySlotDay = hourlyDetailSlot?.day;
  const activitySlotHour = hourlyDetailSlot?.hour;

  useEffect(() => {
    if (subtab !== 1 || hourlyDetailSlot || !hasDigitalActivity) return;
    const cell = getDefaultActivityCell();
    void loadHourlyDetail(cell.day, cell.hour, cell.count);
  }, [getDefaultActivityCell, hasDigitalActivity, hourlyDetailSlot, loadHourlyDetail, subtab]);

  useEffect(() => {
    if (subtab !== 1 || !mapSelectedCell || !hasDigitalActivity) return;
    if (hourlyDetailSlot?.day === mapSelectedCell.day && hourlyDetailSlot?.hour === mapSelectedCell.hour) return;

    void loadHourlyDetail(
      mapSelectedCell.day,
      mapSelectedCell.hour,
      getActivityCellCount(mapSelectedCell.day, mapSelectedCell.hour),
    );
  }, [getActivityCellCount, hasDigitalActivity, hourlyDetailSlot, loadHourlyDetail, mapSelectedCell, subtab]);

  useEffect(() => {
    if (subtab !== 1 || activitySlotDay == null || activitySlotHour == null || !hasDigitalActivity) return;
    const reloadKey = `${eventFilterKey}|${activitySlotDay}|${activitySlotHour}`;
    if (lastActivityFilterKeyRef.current === reloadKey) return;
    void loadHourlyDetail(
      activitySlotDay,
      activitySlotHour,
      getActivityCellCount(activitySlotDay, activitySlotHour),
    );
  }, [activitySlotDay, activitySlotHour, eventFilterKey, getActivityCellCount, hasDigitalActivity, loadHourlyDetail, subtab]);

  useEffect(() => {
    if (subtab !== 0 || !fingerprintMapEntity) return;
    const reloadKey = `${eventFilterKey}|${fingerprintMapEntity}`;
    if (lastFingerprintFilterKeyRef.current === reloadKey) return;
    void loadFingerprintMapEvents(fingerprintMapEntity);
  }, [eventFilterKey, fingerprintMapEntity, loadFingerprintMapEvents, subtab]);

  const getCategoryIcon = (cat: string) => {
    switch (cat) {
      case 'app_usage': return <AppsIcon sx={{ fontSize: 12 }} />;
      case 'browsing': return <BrowsingIcon sx={{ fontSize: 12 }} />;
      case 'communication': return <CommunicationIcon sx={{ fontSize: 12 }} />;
      case 'location_digital': return <LocationIcon sx={{ fontSize: 12 }} />;
      default: return <DigitalIcon sx={{ fontSize: 12 }} />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return { bg: '#f44336', text: '#fff' };
    if (score >= 0.6) return { bg: '#ff9800', text: '#fff' };
    if (score >= 0.4) return { bg: '#ffc107', text: '#000' };
    return { bg: isDark ? '#2d333b' : '#e0e0e0', text: isDark ? '#adbac7' : '#666' };
  };

  const categoryFilters = useMemo(() => {
    const patternCounts: Record<string, number> = { all: digitalPatterns.length };
    digitalPatterns.forEach(p => { patternCounts[p.category] = (patternCounts[p.category] || 0) + 1; });
    const counts = rawActivityCounts && rawActivityCounts.all > 0
      ? {
          all: rawActivityCounts.all,
          app_usage: rawActivityCounts.apps,
          browsing: rawActivityCounts.web,
          communication: rawActivityCounts.comms,
          location_digital: rawActivityCounts.location,
        }
      : patternCounts;
    return [
      { id: 'all', label: 'Total Events', icon: <DigitalIcon sx={{ fontSize: 14 }} />, count: counts.all, color: '#00bcd4' },
      { id: 'app_usage', label: 'Apps', icon: <AppsIcon sx={{ fontSize: 14 }} />, count: counts.app_usage || 0, color: '#4CAF50' },
      { id: 'browsing', label: 'Web', icon: <BrowsingIcon sx={{ fontSize: 14 }} />, count: counts.browsing || 0, color: '#2196F3' },
      { id: 'communication', label: 'Comms', icon: <CommunicationIcon sx={{ fontSize: 14 }} />, count: counts.communication || 0, color: '#FF9800' },
      { id: 'location_digital', label: 'Located Events', icon: <LocationIcon sx={{ fontSize: 14 }} />, count: counts.location_digital || 0, color: '#9C27B0' },
    ];
  }, [digitalPatterns, rawActivityCounts]);

  // ── Shared sub-renderers ──────────────────────────────────────

  const renderTimeline = (height: number = 180, interactive: boolean = false) => (
    timelineData.length > 0 ? (
      <Box>
        <Box sx={{ height, cursor: interactive ? 'pointer' : 'default' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={timelineData} onClick={interactive ? handleTimelineClick : undefined}>
              <defs>
                <linearGradient id="anomGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f85149" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#f85149" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#21262d' : '#eee'} />
              <XAxis dataKey="date" tick={{ fill: isDark ? '#7d8590' : '#666', fontSize: 10 }} />
              <YAxis tick={{ fill: isDark ? '#7d8590' : '#666', fontSize: 10 }} domain={[0, 100]} />
              <RechartsTooltip
                {...rechartsTooltipThemeProps}
                formatter={(value: number, name: string) => {
                  if (name === 'avgScore') return [`${value}%`, 'Avg Deviation Score'];
                  if (name === 'anomalies') return [value, 'Flagged Anomalies'];
                  return [value, name];
                }}
                labelFormatter={interactive ? (label: string) => `${label} — click to drill down` : undefined}
              />
              <ReferenceLine y={60} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Threshold', fill: '#f59e0b', fontSize: 10 }} />
              <Area type="monotone" dataKey="avgScore" stroke="#f85149" fill="url(#anomGrad)" strokeWidth={2}
                activeDot={interactive ? { r: 6, stroke: '#fff', strokeWidth: 2, cursor: 'pointer' } : undefined} />
              <Bar dataKey="anomalies" fill={alpha('#f85149', 0.6)} radius={[2, 2, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        {interactive && selectedDate && selectedDateAnomalies.length > 0 && (
          <Box sx={{
            mt: 1.5, p: 1.5, borderRadius: 1.5,
            bgcolor: isDark ? alpha('#f85149', 0.08) : alpha('#f85149', 0.04),
            border: `1px solid ${alpha('#f85149', 0.3)}`,
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" fontWeight={700} sx={{ color: '#f85149', fontSize: '0.75rem' }}>
                {selectedDateAnomalies.length} flagged anomal{selectedDateAnomalies.length === 1 ? 'y' : 'ies'} on {selectedDate}
              </Typography>
              <Chip label="×" size="small" onClick={() => setSelectedDate(null)}
                sx={{ height: 18, fontSize: '0.6rem', cursor: 'pointer', bgcolor: alpha('#f85149', 0.2), color: '#f85149' }} />
            </Box>
            {selectedDateAnomalies.map((a, i) => renderAnomalyCard(a, i))}
          </Box>
        )}
        {interactive && selectedDate && selectedDateAnomalies.length === 0 && (
          <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: isDark ? '#0d1117' : '#f6f8fa', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">No flagged anomalies on {selectedDate}</Typography>
          </Box>
        )}
      </Box>
    ) : (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography color="text.secondary" variant="caption">No anomaly timeline data</Typography>
      </Box>
    )
  );

  const renderDeviationRow = (profile: typeof entityRanking[0], idx: number) => {
    const scoreColor = getScoreColor(profile.max_anomaly_score);
    return (
      <Box
        key={profile.entity_id}
        onClick={() => onEntitySelect?.(profile.entity_id)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1,
          borderRadius: 1, cursor: 'pointer',
          bgcolor: selectedEntity === profile.entity_id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
          border: selectedEntity === profile.entity_id ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
          transition: 'all 0.15s',
        }}
      >
        <Typography variant="caption" sx={{ width: 16, fontWeight: 600, color: isDark ? '#7d8590' : '#999', fontSize: '0.65rem' }}>
          {idx + 1}
        </Typography>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, color: isDark ? '#e6edf3' : '#24292f', fontSize: '0.75rem' }}>
          {profile.entity_id}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ width: '100%', height: 6, bgcolor: isDark ? '#21262d' : '#e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{
              width: `${Math.min(profile.max_anomaly_score * 100, 100)}%`,
              height: '100%', borderRadius: 3,
              bgcolor: profile.max_anomaly_score >= 0.7 ? '#f85149' : profile.max_anomaly_score >= 0.4 ? '#f59e0b' : '#3fb950',
              transition: 'width 0.3s ease',
            }} />
          </Box>
        </Box>
        <Tooltip title={`Max Deviation Score: ${Math.round(profile.max_anomaly_score * 100)}% | Avg: ${Math.round(profile.avg_anomaly_score * 100)}% | ${profile.anomalous_days}/${profile.total_days_scored} flagged days`}>
          <Chip label={`${Math.round(profile.max_anomaly_score * 100)}%`} size="small"
            sx={{ height: 20, fontWeight: 700, fontSize: '0.65rem', minWidth: 40, bgcolor: scoreColor.bg, color: scoreColor.text }} />
        </Tooltip>
        {profile.anomalous_days > 0 && (
          <Chip label={`${profile.anomalous_days}d`} size="small"
            sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha('#f85149', 0.15), color: '#f85149' }} />
        )}
      </Box>
    );
  };

  const renderAnomalyCard = (anomaly: any, idx: number) => {
    const scoreColor = getScoreColor(anomaly.anomaly_score);
    const factors: ContributingFactor[] = anomaly.contributing_factors || [];
    const isExpanded = expandedAnomaly === idx;
    const rf = anomaly.raw_features || {};
    const parsedFeatures = typeof rf === 'string' ? (() => { try { return JSON.parse(rf); } catch { return {}; } })() : rf;
    const severity = anomaly.anomaly_score >= 0.8 ? 'critical' : anomaly.anomaly_score >= 0.6 ? 'high' : 'medium';
    const severityColor = severity === 'critical' ? '#f85149' : severity === 'high' ? '#ff9800' : '#f59e0b';
    return (
      <Card key={idx} sx={{
        mb: 0.75,
        bgcolor: selectedEntity === anomaly.entity_id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
        border: `1px solid ${selectedEntity === anomaly.entity_id ? theme.palette.primary.main : isDark ? '#30363d' : '#e0e0e0'}`,
        transition: 'all 0.2s',
        '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) },
      }}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => onEntitySelect?.(anomaly.entity_id)}>
            <Box sx={{ flex: 1 }}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: isDark ? '#e6edf3' : '#24292f' }}>
                  {anomaly.entity_id}
                </Typography>
                <Chip label={severity} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha(severityColor, 0.2), color: severityColor }} />
                <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#999' }}>
                  {anomaly.date}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#656d76', display: 'block', mt: 0.25, fontSize: '0.65rem' }}>
                Digital activity deviated significantly from baseline
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Chip label={`${Math.round(anomaly.anomaly_score * 100)}%`} size="small"
                sx={{ height: 22, fontWeight: 700, fontSize: '0.7rem', bgcolor: scoreColor.bg, color: scoreColor.text }} />
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpandedAnomaly(isExpanded ? null : idx); }}
                sx={{ p: 0.25, color: isDark ? '#7d8590' : '#999', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
          <Collapse in={isExpanded} timeout={200}>
            <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${isDark ? '#21262d' : '#eee'}` }}>
              {/* Anomaly metrics */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                {[
                  { label: 'Anomaly Score', value: anomaly.anomaly_score?.toFixed(3), color: severityColor },
                  { label: 'Recon. Error', value: anomaly.reconstruction_error?.toFixed(4), color: '#f59e0b' },
                  { label: 'Active Hrs', value: anomaly.active_hours_count, color: '#58a6ff' },
                ].map((m, mi) => (
                  <Box key={mi} sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: m.color, fontSize: '0.75rem', display: 'block', lineHeight: 1 }}>
                      {m.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#999', fontSize: '0.55rem' }}>
                      {m.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {/* Deviation bar */}
              <Box sx={{ mb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: isDark ? '#7d8590' : '#999' }}>Deviation</Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: severityColor }}>{Math.round(anomaly.anomaly_score * 100)}%</Typography>
                </Box>
                <Box sx={{ height: 4, bgcolor: isDark ? '#21262d' : '#e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ width: `${Math.min(anomaly.anomaly_score * 100, 100)}%`, height: '100%', borderRadius: 2, bgcolor: severityColor }} />
                </Box>
              </Box>
              {/* Contributing factors — from backend comparison to baseline */}
              {factors.length > 0 && (
                <>
                  <Typography variant="caption" fontWeight={600} sx={{ color: isDark ? '#f85149' : '#d32f2f', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, fontSize: '0.7rem' }}>
                    <InfoIcon sx={{ fontSize: 12 }} /> Why this was flagged
                  </Typography>
                  {factors.map((f, fi) => (
                    <Box key={fi} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.4 }}>
                      {f.direction === 'increase'
                        ? <TrendingUpIcon sx={{ fontSize: 14, color: '#f85149' }} />
                        : <TrendingDownIcon sx={{ fontSize: 14, color: '#58a6ff' }} />}
                      <Typography variant="caption" sx={{ flex: 1, fontSize: '0.7rem', color: isDark ? '#adbac7' : '#424242' }}>
                        {f.feature}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isDark ? '#7d8590' : '#999', minWidth: 55, textAlign: 'right' }}>
                        {f.baseline} → {f.actual}
                      </Typography>
                      <Chip size="small"
                        label={`${f.direction === 'increase' ? '+' : ''}${f.pct_change}%`}
                        sx={{
                          height: 18, fontSize: '0.6rem', fontWeight: 700, minWidth: 48,
                          bgcolor: f.direction === 'increase' ? alpha('#f85149', 0.15) : alpha('#58a6ff', 0.15),
                          color: f.direction === 'increase' ? '#f85149' : '#58a6ff',
                        }} />
                    </Box>
                  ))}
                </>
              )}
              {/* Fallback: show raw feature summary when contributing_factors not yet computed */}
              {factors.length === 0 && Object.keys(parsedFeatures).length > 0 && (
                <>
                  <Typography variant="caption" fontWeight={600} sx={{ color: isDark ? '#f85149' : '#d32f2f', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, fontSize: '0.7rem' }}>
                    <InfoIcon sx={{ fontSize: 12 }} /> Activity on this day
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {[
                      { key: 'total_calls', label: 'Calls', icon: <CommunicationIcon sx={{ fontSize: 10 }} /> },
                      { key: 'total_sms', label: 'SMS', icon: <CommunicationIcon sx={{ fontSize: 10 }} /> },
                      { key: 'unique_contacts', label: 'Contacts', icon: <GroupsIcon sx={{ fontSize: 10 }} /> },
                      { key: 'unique_apps', label: 'Apps', icon: <AppsIcon sx={{ fontSize: 10 }} /> },
                      { key: 'total_app_minutes', label: 'App min', icon: <DigitalIcon sx={{ fontSize: 10 }} /> },
                      { key: 'total_browsing_events', label: 'Browsing', icon: <BrowsingIcon sx={{ fontSize: 10 }} /> },
                    ].filter(f => parsedFeatures[f.key] != null && parsedFeatures[f.key] > 0).map((f, fi) => (
                      <Box key={fi} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.3,
                        px: 0.75, py: 0.25, borderRadius: 1,
                        bgcolor: isDark ? '#21262d' : '#f6f8fa',
                        border: `1px solid ${isDark ? '#30363d' : '#e0e0e0'}`,
                      }}>
                        {f.icon}
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: isDark ? '#adbac7' : '#656d76' }}>
                          {f.label}:
                        </Typography>
                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.65rem', color: isDark ? '#e6edf3' : '#24292f' }}>
                          {typeof parsedFeatures[f.key] === 'number' ? Math.round(parsedFeatures[f.key]) : parsedFeatures[f.key]}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  const renderHeatmap = () => (
    <Box>
      <Box sx={{ pl: 4, display: 'flex', mb: 0.5 }}>
        {Array(24).fill(0).map((_, h) => (
          <Typography key={h} variant="caption" sx={{ flex: 1, textAlign: 'center', fontSize: '0.45rem', color: isDark ? '#7d8590' : '#656d76', visibility: h % 3 === 0 ? 'visible' : 'hidden' }}>
            {formatHour(h)}
          </Typography>
        ))}
      </Box>
      {hourlyPatterns.grid.map((dayData, dayIndex) => (
        <Box key={dayIndex} sx={{ display: 'flex', alignItems: 'center', mb: '2px' }}>
          <Typography variant="caption" sx={{ width: 28, fontSize: '0.6rem', color: isDark ? '#7d8590' : '#656d76', fontWeight: 500 }}>
            {dayNames[dayIndex]}
          </Typography>
          <Box sx={{ display: 'flex', flex: 1, gap: '1px' }}>
            {dayData.map((count, hourIndex) => {
              const isSelected = hourlyDetailSlot?.day === dayIndex && hourlyDetailSlot?.hour === hourIndex;
              return (
                <Tooltip key={hourIndex} title={`${count.toLocaleString()} events — ${fullDayNames[dayIndex]} ${formatHour(hourIndex)} · Click for details`} arrow placement="top">
                  <Box
                    onClick={(e) => handleHeatmapClick(e, dayIndex, hourIndex, count)}
                    sx={{
                      flex: 1, height: 14, borderRadius: '2px', cursor: 'pointer',
                      bgcolor: isSelected ? (isDark ? '#58a6ff' : '#0969da') : getHourColor(count, hourlyPatterns.maxCount),
                      transition: 'all 0.15s ease',
                      outline: isSelected ? `2px solid ${isDark ? '#79c0ff' : '#0550ae'}` : 'none',
                      outlineOffset: -1,
                      zIndex: isSelected ? 10 : 'auto',
                      transform: isSelected ? 'scaleY(1.3)' : 'none',
                      '&:hover': { transform: 'scaleY(1.5)', zIndex: 10, outline: `1px solid ${isDark ? '#58a6ff' : '#0969da'}` },
                    }} />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: isDark ? '#7d8590' : '#656d76' }}>Less</Typography>
        {[0, 0.25, 0.5, 0.75, 1].map((intensity, i) => (
          <Box key={i} sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: getHourColor(intensity * hourlyPatterns.maxCount, hourlyPatterns.maxCount) }} />
        ))}
        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: isDark ? '#7d8590' : '#656d76' }}>More</Typography>
      </Box>
      <Popover
        open={Boolean(heatmapAnchor)}
        anchorEl={heatmapAnchor}
        onClose={() => { setHeatmapAnchor(null); setHeatmapCell(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableScrollLock
        slotProps={{
          root: { sx: { pointerEvents: 'none' } },
          paper: {
            sx: { p: 1.5, borderRadius: 2, bgcolor: isDark ? '#161b22' : '#fff', border: `1px solid ${isDark ? '#30363d' : '#e0e0e0'}`, minWidth: 180, pointerEvents: 'auto' },
          },
        }}
      >
        {heatmapCell && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem', color: isDark ? '#e6edf3' : '#24292f' }}>
                {fullDayNames[heatmapCell.day]} at {formatHour(heatmapCell.hour)}
              </Typography>
              <IconButton size="small" onClick={() => { setHeatmapAnchor(null); setHeatmapCell(null); }}
                sx={{ p: 0.25, ml: 1, color: isDark ? '#7d8590' : '#999', '&:hover': { color: isDark ? '#e6edf3' : '#24292f' } }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.75 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: isDark ? '#3fb950' : '#2e7d32', lineHeight: 1 }}>
                {heatmapCell.count.toLocaleString()}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#999' }}>digital events</Typography>
            </Box>
            <Box sx={{ height: 4, bgcolor: isDark ? '#21262d' : '#e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.min((heatmapCell.count / hourlyPatterns.maxCount) * 100, 100)}%`, height: '100%', borderRadius: 2, bgcolor: getHourColor(heatmapCell.count, hourlyPatterns.maxCount) }} />
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#999', display: 'block', mt: 0.5, fontSize: '0.6rem' }}>
              {Math.round((heatmapCell.count / hourlyPatterns.maxCount) * 100)}% of peak activity ({hourlyPatterns.maxCount.toLocaleString()})
            </Typography>
          </Box>
        )}
      </Popover>
    </Box>
  );

  const renderSimilarityPairs = () => {
    const pairs = hasInsights && digitalInsights!.behavioral_similarities.length > 0
      ? digitalInsights!.behavioral_similarities
      : digitalSimilarities.map(s => ({ entity_a: s.entity_a, entity_b: s.entity_b, similarity: s.similarity }));
    return (
      <Box sx={{ ...scrollbarSx }}>
        <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#656d76', display: 'block', mb: 1 }}>
          {hasInsights && digitalInsights!.behavioral_similarities.length > 0
            ? 'From 96-dim digital behavioral embeddings (app, comms, browsing features)'
            : 'From app usage frequency vectors (cosine similarity)'}
        </Typography>
        {pairs.slice(0, 30).map((sim, idx) => (
          <Box key={idx} sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1,
            borderRadius: 1, mb: 0.5,
            border: `1px solid ${isDark ? '#30363d' : '#e0e0e0'}`,
            '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.03) },
            transition: 'all 0.15s',
          }}>
            <Typography variant="caption" fontWeight={600}
              sx={{ color: isDark ? '#58a6ff' : '#0969da', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, fontSize: '0.75rem' }}
              onClick={() => onEntitySelect?.(sim.entity_a)}>{sim.entity_a}</Typography>
            <CompareIcon sx={{ fontSize: 12, color: isDark ? '#7d8590' : '#999' }} />
            <Typography variant="caption" fontWeight={600}
              sx={{ color: isDark ? '#58a6ff' : '#0969da', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, fontSize: '0.75rem' }}
              onClick={() => onEntitySelect?.(sim.entity_b)}>{sim.entity_b}</Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ width: 50, height: 5, bgcolor: isDark ? '#21262d' : '#e0e0e0', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ width: `${sim.similarity * 100}%`, height: '100%', borderRadius: 3,
                bgcolor: sim.similarity >= 0.9 ? '#3fb950' : sim.similarity >= 0.8 ? '#58a6ff' : '#f59e0b' }} />
            </Box>
            <Typography variant="caption" sx={{
              fontWeight: 700, fontSize: '0.7rem', minWidth: 32, textAlign: 'right',
              color: sim.similarity >= 0.9 ? '#3fb950' : sim.similarity >= 0.8 ? '#58a6ff' : '#f59e0b',
            }}>{Math.round(sim.similarity * 100)}%</Typography>
          </Box>
        ))}
        {pairs.length === 0 && (
          <Typography color="text.secondary" textAlign="center" py={2} variant="caption">
            No similar behavioral profiles found
          </Typography>
        )}
      </Box>
    );
  };

  const getPatternTimeLabel = (pattern: DigitalFingerprint) => {
    const metadataTime = pattern.metadata?.time_of_day;
    if (pattern.time_of_day) return pattern.time_of_day;
    if (typeof metadataTime === 'string' && metadataTime) return metadataTime;
    if (pattern.peak_hour === null || pattern.peak_hour === undefined) return null;
    const hour = Number(pattern.peak_hour);
    if (Number.isNaN(hour)) return null;
    return hour >= 5 && hour < 12 ? 'morning' : hour >= 12 && hour < 17 ? 'afternoon' : hour >= 17 && hour < 21 ? 'evening' : 'night';
  };

  const getEntitySummaryFromPatterns = (entityId: string, patterns: DigitalFingerprint[]): DigitalFingerprintSummary => {
    const apps = patterns.filter(p => p.category === 'app_usage').map(p => p.name);
    const sites = patterns.filter(p => p.category === 'browsing').map(p => p.name);
    const comms = patterns.filter(p => p.category === 'communication').map(p => p.description);
    const windows = patterns.map(getPatternTimeLabel).filter(Boolean) as string[];
    const locations = patterns.filter(p => p.category === 'location_digital').map(p => ({
      app: p.name,
      location: p.location_name,
      description: p.description,
      confidence: p.confidence,
    }));
    const confidenceTotal = patterns.reduce((sum, p) => sum + p.confidence, 0);

    return {
      entity_id: entityId,
      course_id: patterns[0]?.course_id ?? null,
      primary_apps: Array.from(new Set(apps)).slice(0, 5),
      top_sites: Array.from(new Set(sites)).slice(0, 5),
      communication_profile: Array.from(new Set(comms)).slice(0, 3),
      active_time_windows: Array.from(new Set(windows)).slice(0, 4),
      location_digital_patterns: locations.slice(0, 4),
      confidence_summary: {
        count: patterns.length,
        avg: patterns.length ? confidenceTotal / patterns.length : 0,
        max: patterns.length ? Math.max(...patterns.map(p => p.confidence)) : 0,
      },
    };
  };

  const handleFingerprintActivityLoad = useCallback((event: React.MouseEvent<HTMLElement>, entityId: string) => {
    event.stopPropagation();
    if (fingerprintMapEntity === entityId) {
      setFingerprintMapEntity(null);
      lastFingerprintFilterKeyRef.current = '';
      onEntitySelect?.(null);
      onDigitalEventsLoad?.([]);
      return;
    }

    onEntitySelect?.(entityId);
    void loadFingerprintMapEvents(entityId);
  }, [fingerprintMapEntity, loadFingerprintMapEvents, onDigitalEventsLoad, onEntitySelect]);

  const renderFingerprintChips = (label: string, values: string[], color: string, icon: React.ReactElement) => (
    values.length > 0 && (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: isDark ? '#7d8590' : '#656d76', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {values.map((value) => (
            <Chip key={`${label}-${value}`} icon={icon} label={value} size="small"
              sx={{ height: 22, fontSize: '0.62rem', fontWeight: 600, bgcolor: alpha(color, 0.14), color, '& .MuiChip-icon': { color: 'inherit' } }} />
          ))}
        </Stack>
      </Box>
    )
  );

  const renderFingerprintCard = (entityId: string, emphasized: boolean = false) => {
    const patterns = effectiveFingerprints[entityId] ?? [];
    const summary = fingerprintSummaries[entityId] ?? getEntitySummaryFromPatterns(entityId, patterns);
    const confidence = Math.round((summary.confidence_summary?.avg ?? 0) * 100);

    return (
      <Box
        key={entityId}
        onClick={(event) => handleFingerprintActivityLoad(event, entityId)}
        sx={{
          ...cardSx,
          cursor: 'pointer',
          height: 'auto',
          mb: 1,
          borderColor: emphasized || selectedEntity === entityId ? theme.palette.primary.main : (isDark ? '#30363d' : '#e0e0e0'),
          bgcolor: emphasized || selectedEntity === entityId ? alpha(theme.palette.primary.main, isDark ? 0.12 : 0.06) : cardSx.bgcolor,
          '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.05) },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
          <Box>
            <Typography variant="body2" fontWeight={800} sx={{ color: isDark ? '#e6edf3' : '#24292f', fontSize: '0.85rem' }}>
              {entityId}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#656d76', fontSize: '0.62rem' }}>
              {summary.confidence_summary?.count ?? patterns.length} fingerprint signals
              {summary.course_id ? ` · ${summary.course_id}` : ''}
            </Typography>
          </Box>
          <Chip
            label={confidence > 0 ? `${confidence}% confidence` : 'fingerprint'}
            size="small"
            sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#79c0ff', 0.18), color: isDark ? '#79c0ff' : '#0969da' }}
          />
        </Box>

        {renderFingerprintChips('App Habits', summary.primary_apps, '#3fb950', <AppsIcon sx={{ fontSize: 11 }} />)}
        {renderFingerprintChips('Web Habits', summary.top_sites, '#58a6ff', <BrowsingIcon sx={{ fontSize: 11 }} />)}
        {renderFingerprintChips('Communication Style', summary.communication_profile, '#f59e0b', <CommunicationIcon sx={{ fontSize: 11 }} />)}
        {renderFingerprintChips('Active Windows', summary.active_time_windows, '#a371f7', <TimelineIcon sx={{ fontSize: 11 }} />)}

        {summary.location_digital_patterns.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: isDark ? '#7d8590' : '#656d76', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Location-Linked Digital Behavior
            </Typography>
            <Stack spacing={0.35}>
              {summary.location_digital_patterns.map((loc, idx) => (
                <Typography key={`${entityId}-loc-${idx}`} variant="caption" sx={{ color: isDark ? '#adbac7' : '#424242', fontSize: '0.66rem' }}>
                  {loc.description}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${isDark ? '#21262d' : '#eee'}` }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: isDark ? '#7d8590' : '#656d76', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Evidence
          </Typography>
          <Stack spacing={0.35}>
            {patterns.slice(0, 5).map((pattern, idx) => (
              <Tooltip
                key={`${entityId}-${idx}-${pattern.name}`}
                title={mapSelectedCell
                  ? `Load ${entityId} digital events for map-selected ${dayNames[mapSelectedCell.day]} ${mapSelectedCell.hour.toString().padStart(2, '0')}:00`
                  : `Load ${entityId} digital events for the current day/hour`}
              >
                <Box
                  onClick={(event) => handleFingerprintActivityLoad(event, entityId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    py: 0.25,
                    px: 0.4,
                    mx: -0.4,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  {getCategoryIcon(pattern.category)}
                  <Typography variant="caption" sx={{ flex: 1, color: isDark ? '#adbac7' : '#656d76', fontSize: '0.65rem' }}>
                    {pattern.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#999', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                    {pattern.occurrence_count.toLocaleString()}x · {Math.round(pattern.confidence * 100)}%
                  </Typography>
                </Box>
              </Tooltip>
            ))}
            {patterns.length === 0 && (
              <Typography variant="caption" color="text.secondary">No derived fingerprint rows for this entity yet.</Typography>
            )}
          </Stack>
        </Box>
      </Box>
    );
  };

  const renderFingerprintView = () => {
    const selectedHasFingerprint = selectedEntity && effectiveFingerprints[selectedEntity]?.length > 0;
    const visibleEntities = selectedHasFingerprint
      ? [selectedEntity, ...fingerprintEntityIds.filter(id => id !== selectedEntity)].filter(Boolean) as string[]
      : fingerprintEntityIds;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={cardSx}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDark ? '#79c0ff' : '#0969da', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FingerprintIcon sx={{ fontSize: 16 }} /> Digital Fingerprints
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#656d76', display: 'block', mb: 1 }}>
            Typical digital identity built from apps, web, communications, active time windows, and location-linked digital behavior.
          </Typography>
          {visibleEntities.length > 0 ? (
            <Box sx={{ maxHeight: 620, overflow: 'auto', ...scrollbarSx }}>
              {visibleEntities.slice(0, 25).map((entityId, idx) => renderFingerprintCard(entityId, idx === 0 && entityId === selectedEntity))}
            </Box>
          ) : rawActivityTotal > 0 ? (
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: isDark ? alpha('#58a6ff', 0.08) : alpha('#0969da', 0.04), border: `1px solid ${alpha('#58a6ff', 0.25)}` }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: isDark ? '#79c0ff' : '#0969da', display: 'block', mb: 0.75 }}>
                Raw digital activity is present... Run digital pattern step 11 to generate fingerprints.
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {categoryFilters.filter(cat => cat.id !== 'all' && cat.count > 0).map((cat) => (
                  <Chip key={cat.id} icon={cat.icon} label={`${cat.count.toLocaleString()} ${cat.label}`} size="small"
                    sx={{ height: 22, fontSize: '0.6rem', fontWeight: 600, bgcolor: alpha(cat.color, 0.15), color: cat.color, '& .MuiChip-icon': { color: 'inherit' } }} />
                ))}
              </Stack>
            </Box>
          ) : (
            <Typography color="text.secondary" textAlign="center" py={3} variant="caption">No digital fingerprint data available for the current filters.</Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderHourlyEventDetail = () => (
    hourlyDetailSlot && (
      <Box sx={cardSx}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#3fb950' : '#2e7d32', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimelineIcon sx={{ fontSize: 16 }} />
              Events in Selected Timeframe
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#656d76', fontSize: '0.62rem' }}>
              All {fullDayNames[hourlyDetailSlot.day]}s at {formatHour(hourlyDetailSlot.hour)} in the active header range
            </Typography>
          </Box>
          <Chip label="x" size="small" onClick={() => { setHourlyDetailSlot(null); setHourlyDetail(null); onDigitalEventsLoad?.([]); }}
            sx={{ height: 18, fontSize: '0.6rem', cursor: 'pointer', bgcolor: isDark ? '#21262d' : '#e0e0e0', color: isDark ? '#7d8590' : '#999' }} />
        </Box>
        {hourlyDetailLoading ? (
          <Box sx={{ py: 2 }}>
            <LinearProgress sx={{ borderRadius: 1 }} />
            <Typography color="text.secondary" textAlign="center" mt={1} variant="caption">Loading events...</Typography>
          </Box>
        ) : hourlyDetail ? (
          <>
            <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
              {[
                { label: 'Calls', count: hourlyDetail.summary.calls, color: '#f85149', icon: <CommunicationIcon sx={{ fontSize: 10 }} /> },
                { label: 'SMS', count: hourlyDetail.summary.sms, color: '#f59e0b', icon: <CommunicationIcon sx={{ fontSize: 10 }} /> },
                { label: 'Apps', count: hourlyDetail.summary.apps, color: '#3fb950', icon: <AppsIcon sx={{ fontSize: 10 }} /> },
                { label: 'Browsing', count: hourlyDetail.summary.browsing, color: '#58a6ff', icon: <BrowsingIcon sx={{ fontSize: 10 }} /> },
              ].filter(s => s.count > 0).map((s, si) => (
                <Chip key={si} icon={s.icon} label={`${s.count} ${s.label}`} size="small"
                  sx={{ height: 22, fontSize: '0.6rem', fontWeight: 600, bgcolor: alpha(s.color, 0.15), color: s.color, '& .MuiChip-icon': { color: 'inherit' } }} />
              ))}
              {hourlyDetail.summary.calls + hourlyDetail.summary.sms + hourlyDetail.summary.apps + hourlyDetail.summary.browsing === 0 && (
                <Typography variant="caption" color="text.secondary">No events recorded in this time slot</Typography>
              )}
            </Box>
            <Box sx={{ maxHeight: 350, overflow: 'auto', ...scrollbarSx }}>
              {hourlyDetail.events.map((evt, ei) => {
                const typeConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
                  call: { color: '#f85149', icon: <CommunicationIcon sx={{ fontSize: 12 }} />, label: 'Call' },
                  sms: { color: '#f59e0b', icon: <CommunicationIcon sx={{ fontSize: 12 }} />, label: 'SMS' },
                  app: { color: '#3fb950', icon: <AppsIcon sx={{ fontSize: 12 }} />, label: 'App' },
                  browsing: { color: '#58a6ff', icon: <BrowsingIcon sx={{ fontSize: 12 }} />, label: 'Web' },
                };
                const tc = typeConfig[evt.type] || typeConfig.app;
                const ts = evt.timestamp ? new Date(evt.timestamp) : null;
                return (
                  <Box key={ei} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, px: 0.75,
                    borderRadius: 1, mb: '2px',
                    '&:hover': { bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02) },
                    cursor: 'pointer',
                  }}
                    onClick={() => {
                      onEntitySelect?.(evt.entity_id);
                      if (evt.lat != null && evt.lon != null) {
                        onDigitalEventSelect?.(evt);
                      }
                    }}>
                    <Box sx={{ color: tc.color, display: 'flex', alignItems: 'center' }}>{tc.icon}</Box>
                    <Chip label={tc.label} size="small"
                      sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, minWidth: 30, bgcolor: alpha(tc.color, 0.15), color: tc.color }} />
                    <Typography variant="caption" fontWeight={600} sx={{ color: isDark ? '#58a6ff' : '#0969da', fontSize: '0.7rem', minWidth: 65 }}>
                      {evt.entity_id}
                    </Typography>
                    <Typography variant="caption" sx={{ flex: 1, fontSize: '0.65rem', color: isDark ? '#adbac7' : '#424242', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {evt.detail}
                    </Typography>
                    {evt.contact && (
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: isDark ? '#7d8590' : '#999' }}>
                        {evt.contact.slice(-4).padStart(evt.contact.length > 4 ? 8 : evt.contact.length, '*')}
                      </Typography>
                    )}
                    {ts && (
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: isDark ? '#7d8590' : '#bbb', minWidth: 40, textAlign: 'right' }}>
                        {ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" textAlign="center" display="block" py={2}>
            Could not load event details
          </Typography>
        )}
      </Box>
    )
  );

  const renderActivityView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={cardSx}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDark ? '#3fb950' : '#2e7d32', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TimelineIcon sx={{ fontSize: 16 }} /> Digital Activity Timeline
        </Typography>
        <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#656d76', display: 'block', mb: 1, fontSize: '0.65rem' }}>
          Raw digital event volume by day of week and hour. Activity auto-loads the map-selected timeframe, or the current day/hour when no map cell is selected.
        </Typography>
        {renderHeatmap()}
      </Box>
      {renderHourlyEventDetail()}
    </Box>
  );

  const renderDeviationView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {hasInsights ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {[
              { label: 'Entities Scored', value: digitalInsights!.summary.total_entities_scored, color: '#58a6ff' },
              { label: 'Days Analyzed', value: digitalInsights!.summary.total_days_analyzed, color: '#3fb950' },
              { label: 'Flagged Anomalies', value: anomalyCount, color: anomalyCount > 0 ? '#f85149' : '#7d8590' },
              { label: 'Avg Flag Rate', value: `${Math.round(digitalInsights!.summary.avg_anomaly_rate * 100)}%`, color: '#f59e0b' },
            ].map((s, i) => (
              <Box key={i} sx={{ ...cardSx, textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#999', fontSize: '0.6rem' }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={cardSx}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDark ? '#f85149' : '#d32f2f', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimelineIcon sx={{ fontSize: 16 }} /> Deviation Score Timeline
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#7d8590' : '#656d76', display: 'block', mb: 1 }}>
              Average digital deviation score from the Isolation Forest model. Scores can rise without producing a flagged anomaly.
            </Typography>
            {renderTimeline(240, true)}
          </Box>
          <Box sx={cardSx}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDark ? '#ffa657' : '#d97706', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningIcon sx={{ fontSize: 16 }} /> Top Deviations
            </Typography>
            <Box sx={{ ...scrollbarSx, maxHeight: 320, overflow: 'auto' }}>
              {entityRanking.map((p, i) => renderDeviationRow(p, i))}
              {entityRanking.length === 0 && <Typography color="text.secondary" textAlign="center" py={2} variant="caption">No entities with enough scored days</Typography>}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ ...cardSx, textAlign: 'center', py: 3 }}>
          <WarningIcon sx={{ fontSize: 32, color: isDark ? '#7d8590' : '#999', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {hasDigitalActivity ? 'Digital activity found, deviation model not yet computed' : 'Digital deviation model not yet computed'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasDigitalActivity
              ? 'Raw digital activity is present, but these entities have not been scored by the digital anomaly model yet.'
              : 'Run the embedding pipeline to generate deviation scoring.'}
          </Typography>
        </Box>
      )}
      <Box sx={cardSx}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDark ? '#f85149' : '#d32f2f', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningIcon sx={{ fontSize: 16 }} /> Flagged Digital Anomalies
          {hasInsights && <Chip label={filteredAnomalies.length} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem', bgcolor: alpha('#f85149', 0.2), color: '#f85149' }} />}
        </Typography>
        <Box sx={{ ...scrollbarSx, maxHeight: 500, overflow: 'auto' }}>
          {filteredAnomalies.length > 0
            ? filteredAnomalies.map((a, i) => renderAnomalyCard(a, i))
            : <Typography color="text.secondary" textAlign="center" py={3} variant="caption">{hasInsights ? 'No flagged digital anomalies for the current filters.' : 'Run the embedding pipeline to detect deviations.'}</Typography>}
        </Box>
      </Box>
    </Box>
  );

  const renderSimilarityView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={cardSx}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: isDark ? '#a371f7' : '#7b1fa2', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CompareIcon sx={{ fontSize: 16 }} /> Digital Similarity
          <Chip label={hasInsights ? digitalInsights!.behavioral_similarities.length : digitalSimilarities.length}
            size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem', bgcolor: alpha('#a371f7', 0.2), color: '#a371f7' }} />
        </Typography>
        <Box sx={{ maxHeight: 520, overflow: 'auto', ...scrollbarSx }}>
          {renderSimilarityPairs()}
        </Box>
      </Box>
    </Box>
  );

  // ── Loading state ─────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <LinearProgress sx={{ borderRadius: 1 }} />
        <Typography color="text.secondary" textAlign="center" mt={2}>Analyzing digital behavioral patterns...</Typography>
      </Box>
    );
  }

  // ── Main render ───────────────────────────────────────────────

  const anomalyCount = filteredAnomalies.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Header: category filters as minimal mini-cards + summary chips */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', pb: 0.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${categoryFilters.length}, minmax(56px, 1fr))`, gap: 0.5, flex: '1 1 auto', minWidth: 0 }}>
          {categoryFilters.map((cat) => (
            <Tooltip key={cat.id} title={`${cat.label}: ${cat.count.toLocaleString()} digital events`}>
              <Box
                sx={{
                  py: 0.75,
                  px: 0.5,
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid',
                  borderColor: isDark ? '#30363d' : '#e0e0e0',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontWeight: 600,
                    color: isDark ? '#7d8590' : '#999',
                    display: 'block',
                    lineHeight: 1,
                    mb: 0.4,
                  }}
                >
                  {cat.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: isDark ? '#e6edf3' : '#24292f',
                    lineHeight: 1,
                  }}
                >
                  {cat.count.toLocaleString()}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* Subtab bar */}
      <Tabs value={subtab} onChange={(_, v) => {
        if (subtab === 1 && v !== 1) onDigitalEventsLoad?.([]);
        if (subtab === 0 && v !== 0) {
          setFingerprintMapEntity(null);
          onDigitalEventsLoad?.([]);
        }
        setSubtab(v);
      }}
        sx={{
          minHeight: 32,
          '& .MuiTab-root': { minHeight: 32, py: 0.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, textTransform: 'none' },
          '& .MuiTabs-indicator': { height: 2 },
          borderBottom: `1px solid ${isDark ? '#30363d' : '#e0e0e0'}`,
        }}>
        <Tab icon={<FingerprintIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Fingerprints" />
        <Tab icon={<TimelineIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Activity" />
        <Tab icon={<AnomalyIcon sx={{ fontSize: 14 }} />} iconPosition="start"
          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Deviations{anomalyCount > 0 && <Chip label={anomalyCount} size="small" color="error" sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />}</Box>} />
        <Tab icon={<CompareIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Similarity" />
      </Tabs>
      {subtab === 0 && renderFingerprintView()}
      {subtab === 1 && renderActivityView()}
      {subtab === 2 && renderDeviationView()}
      {subtab === 3 && renderSimilarityView()}
    </Box>
  );
};

export default DigitalInsightsPanel;
